// frontend/src/pages/org/channels-page.tsx
// Read DESIGN.md before modifying. Blue info Alert pattern for state banners;
// Title Case button labels; Enabled/Disabled state words (never Active/Inactive);
// no em-dashes; rounded-2xl border table container (billing-history pattern).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Cable, ChevronsUpDown, Info, MoreVertical, Search } from 'lucide-react';
import { Combobox } from '@base-ui/react/combobox';
import { toast } from 'sonner';
import { formatInstagramHandle } from '@/lib/channel-display';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MoveChannelDialog } from '@/components/channels/move-channel-dialog';
import { EditDestinationDialog } from '@/components/channels/edit-destination-dialog';
import { OrgDestinationCard } from '@/components/onboarding/org-destination-card';
import { useOrg } from '@/components/org-layout';
import {
  useApplyOrgChannelDestinations,
  useMoveOrgChannel,
  useOrgChannels,
  useOrgChannelWorkspaces,
  useOrgDestination,
  useSetCachedOrgDestinationUrl,
  type ChannelDestinationRow,
} from './channels-queries';
import { PageContainer } from '@/components/page-container';
import { midEllipsisUrl } from '@/lib/mid-ellipsis';

function channelTypeLabel(type: ChannelDestinationRow['type']): string {
  if (type === 'whatsapp') return 'WhatsApp';
  if (type === 'instagram') return 'Instagram';
  return 'Facebook';
}

const TYPE_FILTER_ITEMS = [
  { value: 'all', label: 'All Types' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
] as const;

const STATUS_FILTER_ITEMS = [
  { value: 'active', label: 'Connected' },
  { value: 'disconnected', label: 'Disconnected' },
  { value: 'all', label: 'All Statuses' },
] as const;

type StatusFilter = (typeof STATUS_FILTER_ITEMS)[number]['value'];

const PAGE_SIZE = 50;

function errorMessage(error: unknown, fallback: string): string {
  return error && typeof error === 'object' && 'userMessage' in error
    ? ((error as { userMessage?: string }).userMessage ?? fallback)
    : fallback;
}

function headerClassName(columnId: string): string | undefined {
  if (columnId === 'select') return 'w-10';
  if (columnId === 'workspaceName') return 'w-[20%]';
  if (columnId === 'displayName') return 'w-[26%]';
  if (columnId === 'type') return 'w-[11%]';
  if (columnId === 'channelStatus') return 'w-[12%]';
  if (columnId === 'actions') return 'w-12 text-right';
  return undefined;
}

function cellClassName(columnId: string): string | undefined {
  if (columnId === 'workspaceName') return 'font-medium';
  if (columnId === 'type' || columnId === 'webhookUrl') return 'text-muted-foreground';
  if (columnId === 'actions') return 'text-right';
  return undefined;
}

/**
 * Display name for a ChannelDestinationRow. Mirrors channelDisplayName() logic
 * without a cast: ChannelDestinationRow is a different structural type from
 * Channel even though all the fields channelDisplayName reads are present.
 */
function rowDisplayName(row: ChannelDestinationRow): string {
  if (row.type === 'instagram') {
    const trimmedName = row.instagramProfileName?.trim();
    if (trimmedName) return trimmedName;
    return row.instagramUsername ? formatInstagramHandle(row.instagramUsername) : 'Instagram Channel';
  }
  if (row.type === 'whatsapp') {
    const base = row.whatsappWabaName?.trim() || 'WhatsApp Channel';
    const phone = row.whatsappDisplayPhoneNumber?.replace(/[+\s-]/g, '');
    return phone ? `${base} · ${phone}` : base;
  }
  return row.facebookPageName?.trim() || 'Facebook Page';
}

/**
 * Channels page (Plan 3 Task 4): org-destination editor at the top, plus a
 * searchable table of every channel in the org with per-row selection and
 * bulk destination apply (scope=selected or scope=all, mode=org or mode=clear).
 *
 * "Set To Organization Destination" is disabled when the org destination URL
 * is unset so the page never triggers the backend's ORG_DESTINATION_UNSET 409.
 *
 * After the card saves or clears, an AlertDialog offers to propagate the
 * change to all existing channels immediately.
 */
export function OrgChannelsPage({ kind = 'customer' }: { kind?: 'team' | 'customer' } = {}) {
  const { orgId } = useParams<{ orgId: string }>();
  // The Channels page serves two surfaces: 'customer' (SaaS) and 'team'
  // (Workspaces). Only the workspace-kind scoping + wording differs.
  const isCustomer = kind === 'customer';
  const filterNoun = isCustomer ? 'customer' : 'workspace';
  const allFilterLabel = isCustomer ? 'All Customers' : 'All Workspaces';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // Debounced copy of `search` so typing fires one fetch, not one per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // 'all' = no filter. Workspace filter is keyed by publicId; type by discriminator.
  const [workspaceFilter, setWorkspaceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  // Disconnected rows (e.g. a number re-onboarded into another customer) are
  // hidden by default so they don't read as duplicate live channels.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // "Select all N matching" mode: bulk apply targets the whole filtered set
  // server-side (scope='all' + filter) instead of the per-page selection.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);
  // The row whose Move / Edit Destination dialog is open (null = closed).
  const [moveRow, setMoveRow] = useState<ChannelDestinationRow | null>(null);
  const [destRow, setDestRow] = useState<ChannelDestinationRow | null>(null);
  // Bulk modals acting on the current selection.
  const [bulkDestOpen, setBulkDestOpen] = useState(false);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  // 'org' = save-prompt open; 'clear' = clear-prompt open; null = closed
  const [applyPrompt, setApplyPrompt] = useState<'org' | 'clear' | null>(null);

  const channelFilters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      q: debouncedSearch || undefined,
      workspaceId: workspaceFilter !== 'all' ? workspaceFilter : undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined,
      kind,
      status: statusFilter,
    }),
    [page, debouncedSearch, workspaceFilter, typeFilter, kind, statusFilter],
  );
  const { openConnectChannel } = useOrg();
  const channelsQuery = useOrgChannels(orgId, channelFilters);
  const destinationQuery = useOrgDestination(orgId);
  const workspacesQuery = useOrgChannelWorkspaces(orgId, true, kind);
  // Move targets span BOTH kinds so a mis-connected channel can be moved to the
  // other side (team <-> customer). The filter still uses this surface's kind.
  const otherKind: 'team' | 'customer' = isCustomer ? 'team' : 'customer';
  const otherWorkspacesQuery = useOrgChannelWorkspaces(orgId, true, otherKind);
  const moveChannelMutation = useMoveOrgChannel(orgId);
  const applyDestinationsMutation = useApplyOrgChannelDestinations(orgId);
  const setCachedOrgDestinationUrl = useSetCachedOrgDestinationUrl(orgId);

  // rows = the CURRENT page only; total is the full filtered count. The list is
  // server-paginated + server-filtered so the client never holds 1000s of rows.
  const rows = useMemo(() => channelsQuery.data?.rows ?? [], [channelsQuery.data?.rows]);
  const total = channelsQuery.data?.total ?? 0;
  const workspaces = useMemo(
    () => workspacesQuery.data?.workspaces ?? [],
    [workspacesQuery.data?.workspaces],
  );
  // Move-dialog targets: this surface's workspaces + the other kind's, each
  // tagged with its kind so the picker can badge them.
  const moveTargets = useMemo(
    () => [
      ...(workspacesQuery.data?.workspaces ?? []).map((w) => ({ ...w, kind })),
      ...(otherWorkspacesQuery.data?.workspaces ?? []).map((w) => ({ ...w, kind: otherKind })),
    ],
    [workspacesQuery.data?.workspaces, otherWorkspacesQuery.data?.workspaces, kind, otherKind],
  );
  const loading = channelsQuery.isPending;
  const error = channelsQuery.isError;
  const applying = applyDestinationsMutation.isPending;
  const moving = moveChannelMutation.isPending || bulkMoving;
  // null = still loading from GET /destination
  const orgDestHasUrl = destinationQuery.data
    ? destinationQuery.data.webhookUrl !== null
    : null;
  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  // Debounce the search box (250ms) before it drives a fetch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter/search change resets to page 1 and drops select-all-matching.
  useEffect(() => {
    setPage(1);
    setSelectAllMatching(false);
    setSelected(new Set());
  }, [debouncedSearch, workspaceFilter, typeFilter, statusFilter]);

  // Move a single channel to another workspace in the same org. Refreshes the
  // table and workspace list so the row's new workspace + counts stay accurate.
  async function moveChannel(channelPublicId: string, targetWorkspacePublicId: string) {
    if (!orgId || moving) return;
    try {
      await moveChannelMutation.mutateAsync({ channelPublicId, targetWorkspacePublicId });
      toast.success('Channel moved successfully');
      setMoveRow(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to move channel'));
    }
  }

  // Bulk-move every explicitly-selected channel to one target workspace. The
  // move endpoint is per-channel, so we fan out sequentially and report a single
  // toast. Bounded to the checkbox selection (not select-all-matching), so this
  // never fans out thousands of requests.
  async function bulkMoveChannels(targetWorkspacePublicId: string) {
    if (!orgId || moving) return;
    const ids = selectedPublicIds;
    if (ids.length === 0) return;
    setBulkMoving(true);
    let failed = 0;
    try {
      for (const channelPublicId of ids) {
        try {
          await moveChannelMutation.mutateAsync({
            channelPublicId,
            targetWorkspacePublicId,
          });
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) {
        toast.success(`Moved ${ids.length} ${ids.length === 1 ? 'channel' : 'channels'} successfully`);
      } else {
        toast.error(`Moved ${ids.length - failed} of ${ids.length}; ${failed} failed`);
      }
      setBulkMoveOpen(false);
      setSelected(new Set());
    } finally {
      setBulkMoving(false);
    }
  }

  // Workspace filter options come from the org's workspace list (loaded once),
  // not the current page, so every workspace is selectable even when its
  // channels aren't on screen.
  // "All Customers" plus the distinct customers holding channels; held
  // client-side so the combobox fuzzy-filters in memory.
  const customerFilterItems = useMemo(
    () => [
      { publicId: 'all', name: allFilterLabel },
      ...workspaces.map((w) => ({ publicId: w.publicId, name: w.name })),
    ],
    [workspaces, allFilterLabel],
  );
  const selectedCustomerFilter =
    customerFilterItems.find((c) => c.publicId === workspaceFilter) ?? customerFilterItems[0];

  // Selection spans pages (keyed by publicId); the header checkbox only governs
  // the rows currently on screen. Disconnected rows can't be moved or given a
  // destination (server rejects/skips them), so they are never selectable.
  const selectableRows = useMemo(
    () => rows.filter((r) => r.channelStatus === 'connected'),
    [rows],
  );
  const allPageSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.publicId));
  const somePageSelected = selectableRows.some((r) => selected.has(r.publicId));
  const headerChecked: boolean | 'indeterminate' = allPageSelected
    ? true
    : somePageSelected
      ? 'indeterminate'
      : false;

  const toggleAll = useCallback(() => {
    setSelectAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) selectableRows.forEach((r) => next.delete(r.publicId));
      else selectableRows.forEach((r) => next.add(r.publicId));
      return next;
    });
  }, [allPageSelected, selectableRows]);

  const toggleRow = useCallback((publicId: string) => {
    setSelectAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  }, []);

  async function applyBulk(
    scope: 'all' | 'selected',
    mode: 'org' | 'clear' | 'custom',
    channelPublicIds?: string[],
    filter?: { q?: string; type?: string; workspaceId?: string; status?: string },
    url?: string,
    verifyToken?: string,
  ): Promise<boolean> {
    if (!orgId) return false;
    try {
      await applyDestinationsMutation.mutateAsync({
        scope,
        mode,
        url,
        verifyToken,
        channelPublicIds,
        kind,
        filter,
      });
      setSelected(new Set());
      setSelectAllMatching(false);
      toast.success('Destination applied successfully');
      return true;
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to apply destination'));
      return false;
    }
  }

  // Per-row Edit Destination confirm: apply to just this channel, close on success.
  async function handleEditDestinationConfirm(
    mode: 'org' | 'clear' | 'custom',
    url?: string,
    verifyToken?: string,
  ) {
    if (!destRow) return;
    const ok = await applyBulk('selected', mode, [destRow.publicId], undefined, url, verifyToken);
    if (ok) setDestRow(null);
  }

  function onOrgDestinationSaved() {
    setCachedOrgDestinationUrl(true);
    setApplyPrompt('org');
  }

  function onOrgDestinationCleared() {
    setCachedOrgDestinationUrl(false);
    setApplyPrompt('clear');
  }

  const selectedPublicIds = [...selected];
  const hasSelection = selectAllMatching || selectedPublicIds.length > 0;
  const selectedCount = selectAllMatching ? total : selectedPublicIds.length;
  // The org-destination choice in the Edit Destinations modal is gated on a URL
  // being set; the modal explains it inline.
  const canSetOrgDest = orgDestHasUrl === true;
  // Lets the dialog seed "org" vs "custom" for a channel that already has a url.
  const orgWebhookUrl = destinationQuery.data?.webhookUrl ?? null;

  // The active filter, forwarded to scope='all' applies so "select all matching"
  // hits exactly the filtered set.
  const currentFilter = {
    q: debouncedSearch || undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    workspaceId: workspaceFilter !== 'all' ? workspaceFilter : undefined,
    status: statusFilter,
  };

  // Bulk apply dispatches to scope='all'+filter (select-all-matching) or
  // scope='selected'+ids (per-page picks). Returns whether the apply succeeded
  // so the Edit Selected modal can close itself on success.
  function applyBulkAction(
    mode: 'org' | 'clear' | 'custom',
    url?: string,
    verifyToken?: string,
  ): Promise<boolean> {
    if (selectAllMatching) return applyBulk('all', mode, undefined, currentFilter, url, verifyToken);
    return applyBulk('selected', mode, selectedPublicIds, undefined, url, verifyToken);
  }

  const columns = useMemo<ColumnDef<ChannelDestinationRow>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <Checkbox
            aria-label="Select all channels"
            checked={headerChecked}
            disabled={selectableRows.length === 0}
            onCheckedChange={toggleAll}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={`Select ${rowDisplayName(row.original)}`}
            checked={selected.has(row.original.publicId)}
            disabled={row.original.channelStatus !== 'connected'}
            onCheckedChange={() => toggleRow(row.original.publicId)}
          />
        ),
      },
      {
        id: 'displayName',
        header: 'Channel',
        cell: ({ row }) => (
          <span className="block truncate" title={rowDisplayName(row.original)}>
            {rowDisplayName(row.original)}
          </span>
        ),
      },
      {
        accessorKey: 'workspaceName',
        header: isCustomer ? 'Customer' : 'Workspace',
        cell: ({ row }) => (
          <span className="block truncate" title={row.original.workspaceName}>
            {row.original.workspaceName}
          </span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => channelTypeLabel(row.original.type),
      },
      {
        accessorKey: 'channelStatus',
        header: 'Status',
        cell: ({ row }) =>
          row.original.channelStatus === 'connected' ? (
            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-300">
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
              {row.original.channelStatus === 'archived' ? 'Archived' : 'Disconnected'}
            </span>
          ),
      },
      {
        accessorKey: 'webhookUrl',
        header: 'Destination',
        cell: ({ row }) => (
          <span className="block truncate" title={row.original.webhookUrl ?? 'HookMyApp CLI'}>
            {row.original.webhookUrl ? midEllipsisUrl(row.original.webhookUrl) : 'HookMyApp CLI'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) =>
          row.original.channelStatus !== 'connected' ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={`Actions for ${rowDisplayName(row.original)}`}
                />
              }
            >
              <MoreVertical className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setMoveRow(row.original)}>
                Move Channel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDestRow(row.original)}>
                Edit Destination
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [headerChecked, selected, toggleAll, toggleRow, isCustomer, selectableRows.length],
  );
  const pagination = useMemo(
    () => ({ pageIndex: page - 1, pageSize: PAGE_SIZE }),
    [page],
  );
  const columnFilters = useMemo(
    () => [
      ...(workspaceFilter !== 'all'
        ? [{ id: 'workspaceName', value: workspaceFilter }]
        : []),
      ...(typeFilter !== 'all' ? [{ id: 'type', value: typeFilter }] : []),
    ],
    [workspaceFilter, typeFilter],
  );
  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.publicId,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualPagination: true,
    pageCount: totalPages,
    state: {
      globalFilter: debouncedSearch,
      columnFilters,
      pagination,
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      setPage(next.pageIndex + 1);
    },
  });

  return (
    <PageContainer variant="fill" className="gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Channels</h1>
      </div>

      {orgId && (
        <OrgDestinationCard
          orgId={orgId}
          onSaved={onOrgDestinationSaved}
          onCleared={onOrgDestinationCleared}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-56">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              aria-label="Search channels"
              placeholder="Search channels..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* base-ui Combobox with the default contains-matcher: fuzzy-filters
              the customer list in memory. Selecting "All Customers" clears it. */}
          <Combobox.Root
            items={customerFilterItems}
            value={selectedCustomerFilter}
            onValueChange={(item: { publicId: string; name: string } | null) =>
              setWorkspaceFilter(item?.publicId ?? 'all')
            }
            itemToStringLabel={(item: { publicId: string; name: string } | null) =>
              item && item.publicId !== 'all' ? item.name : ''
            }
            isItemEqualToValue={(
              a: { publicId: string } | null,
              b: { publicId: string } | null,
            ) => a?.publicId === b?.publicId}
          >
            <div className="relative w-full sm:w-48">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Combobox.Input
                aria-label={`Filter by ${filterNoun}`}
                placeholder={allFilterLabel}
                className="flex h-9 w-full rounded-md border border-input bg-transparent py-1 pr-8 pl-8 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
              <Combobox.Trigger
                aria-label={`Open ${filterNoun} list`}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground outline-none hover:text-foreground"
              >
                <ChevronsUpDown className="size-4" />
              </Combobox.Trigger>
            </div>
            <Combobox.Portal>
              <Combobox.Positioner positionMethod="fixed" align="start" side="bottom" sideOffset={4} className="z-50">
                <Combobox.Popup className="w-(--anchor-width) max-h-60 overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none">
                  <Combobox.Empty className="px-2 py-6 text-center text-sm text-muted-foreground empty:hidden">
                    No {filterNoun}s match your search
                  </Combobox.Empty>
                  <Combobox.List>
                    {(item: { publicId: string; name: string }) => (
                      <Combobox.Item
                        key={item.publicId}
                        value={item}
                        className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                      >
                        <span className="truncate">{item.name}</span>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Root>

          <Select
            items={TYPE_FILTER_ITEMS}
            value={typeFilter}
            onValueChange={(v) => setTypeFilter(v ?? 'all')}
          >
            <SelectTrigger aria-label="Filter by type" className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_FILTER_ITEMS.map((i) => (
                <SelectItem key={i.value} value={i.value}>
                  {i.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            items={STATUS_FILTER_ITEMS}
            value={statusFilter}
            onValueChange={(v) => setStatusFilter((v as StatusFilter | null) ?? 'active')}
          >
            <SelectTrigger aria-label="Filter by status" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_ITEMS.map((i) => (
                <SelectItem key={i.value} value={i.value}>
                  {i.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Bulk actions for the selected rows. A disabled button swallows
              pointer events, so each tooltip's span wrapper is the hover target. */}
          <div className="sm:ml-auto flex items-center gap-2">
            {hasSelection && (
              <span className="text-sm text-muted-foreground">{selectedCount} selected</span>
            )}
            <Tooltip>
              <TooltipTrigger render={<span className="inline-flex" />}>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selectedPublicIds.length === 0 || moving || applying}
                  onClick={() => setBulkMoveOpen(true)}
                >
                  Move Channels
                </Button>
              </TooltipTrigger>
              {selectedPublicIds.length === 0 && (
                <TooltipContent>
                  {selectAllMatching
                    ? 'Move acts on individually selected channels, not select-all.'
                    : 'Select channels first.'}
                </TooltipContent>
              )}
            </Tooltip>

            <Tooltip>
              {/* aria-label lets tests and AT target the hoverable wrapper of the
                  (possibly disabled) button without structural locators. */}
              <TooltipTrigger
                render={<span className="inline-flex" aria-label="Edit Destinations bulk action" />}
              >
                <Button
                  size="sm"
                  disabled={!hasSelection || applying}
                  onClick={() => setBulkDestOpen(true)}
                >
                  Edit Destinations
                </Button>
              </TooltipTrigger>
              {!hasSelection && <TooltipContent>Select channels first.</TooltipContent>}
            </Tooltip>
          </div>
        </div>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border">
            <Table className="table-fixed max-md:min-w-[44rem]">
              <TableHeader className="[&_tr]:border-b [&_tr]:bg-card [&_th]:bg-card">
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead className="w-[20%]">Workspace</TableHead>
                  <TableHead className="w-[26%]">Channel</TableHead>
                  <TableHead className="w-[11%]">Type</TableHead>
                  <TableHead className="w-[12%]">Status</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="size-4 rounded" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="ml-auto size-4 rounded" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : error ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Unable to load channels.</p>
            <Button variant="outline" size="sm" onClick={() => void channelsQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          debouncedSearch || workspaceFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'active' ? (
            <Alert className="border-blue-500/30 bg-blue-500/5">
              <Info className="text-blue-500" />
              <AlertDescription>No channels match your filters.</AlertDescription>
            </Alert>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
                <div className="rounded-full bg-muted p-3">
                  <Cable className="size-6 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-semibold">
                    Get access to your {filterNoun} channels
                  </h3>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Connect a channel to start sending and receiving messages.
                  </p>
                </div>
                <Button onClick={() => openConnectChannel({ customerOnly: isCustomer })}>
                  Connect Channel
                </Button>
              </CardContent>
            </Card>
          )
        ) : (
          <div className="min-h-0 flex-1 overflow-auto rounded-2xl border [&>[data-slot=table-container]]:overflow-visible max-md:max-h-[500px]">
            <Table className="table-fixed max-md:min-w-[44rem]">
              <TableHeader className="sticky top-0 z-10 [&_tr]:border-b [&_tr]:bg-card [&_tr]:hover:bg-card [&_th]:bg-card">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={headerClassName(header.column.id)}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cellClassName(cell.column.id)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {!loading && !error && total > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <span>
                {(page - 1) * PAGE_SIZE + 1}-{Math.min(total, page * PAGE_SIZE)} of {total}
              </span>
              {selectAllMatching ? (
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={() => {
                    setSelectAllMatching(false);
                    setSelected(new Set());
                  }}
                >
                  Clear selection
                </button>
              ) : (
                allPageSelected &&
                statusFilter === 'active' &&
                total > rows.length && (
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => setSelectAllMatching(true)}
                  >
                    Select all {total} matching
                  </button>
                )
              )}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!table.getCanPreviousPage() || loading}
                onClick={() => table.previousPage()}
              >
                Previous
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!table.getCanNextPage() || loading}
                onClick={() => table.nextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* After saving the org destination: offer to copy it to all channels */}
      <AlertDialog
        open={applyPrompt === 'org'}
        onOpenChange={(open) => {
          if (!open) setApplyPrompt(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply To All Channels?</AlertDialogTitle>
            <AlertDialogDescription>
              Copy this destination to all existing channels?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setApplyPrompt(null);
                void applyBulk('all', 'org');
              }}
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* After clearing the org destination: offer to switch all channels to HookMyApp CLI */}
      <AlertDialog
        open={applyPrompt === 'clear'}
        onOpenChange={(open) => {
          if (!open) setApplyPrompt(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Channel Destinations?</AlertDialogTitle>
            <AlertDialogDescription>
              Clear the webhook destination from all existing channels and switch them to HookMyApp CLI?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setApplyPrompt(null);
                void applyBulk('all', 'clear');
              }}
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {moveRow && (
        <MoveChannelDialog
          open
          onOpenChange={(next) => !next && setMoveRow(null)}
          channelName={rowDisplayName(moveRow)}
          currentWorkspacePublicId={moveRow.workspacePublicId}
          workspaces={moveTargets}
          moving={moving}
          onConfirm={(targetWs) => void moveChannel(moveRow.publicId, targetWs)}
        />
      )}

      {destRow && (
        <EditDestinationDialog
          open
          onOpenChange={(next) => !next && setDestRow(null)}
          channelName={rowDisplayName(destRow)}
          currentWebhookUrl={destRow.webhookUrl}
          orgWebhookUrl={orgWebhookUrl}
          canSetOrgDest={canSetOrgDest}
          applying={applying}
          onConfirm={(mode, url, verifyToken) =>
            void handleEditDestinationConfirm(mode, url, verifyToken)
          }
        />
      )}

      {bulkMoveOpen && (
        <MoveChannelDialog
          open
          onOpenChange={(next) => !next && setBulkMoveOpen(false)}
          channelName={`${selectedPublicIds.length} selected ${selectedPublicIds.length === 1 ? 'channel' : 'channels'}`}
          // Mixed sources, so exclude nothing; every workspace is a valid target.
          currentWorkspacePublicId=""
          workspaces={moveTargets}
          moving={moving}
          onConfirm={(targetWs) => void bulkMoveChannels(targetWs)}
        />
      )}

      {bulkDestOpen && (
        <EditDestinationDialog
          open
          onOpenChange={(next) => !next && setBulkDestOpen(false)}
          channelName={`${selectedCount} selected ${selectedCount === 1 ? 'channel' : 'channels'}`}
          // Selection may mix destinations, so default to the CLI choice.
          currentWebhookUrl={null}
          orgWebhookUrl={orgWebhookUrl}
          canSetOrgDest={canSetOrgDest}
          applying={applying}
          onConfirm={(mode, url, verifyToken) => {
            void applyBulkAction(mode, url, verifyToken).then((ok) => ok && setBulkDestOpen(false));
          }}
        />
      )}
    </PageContainer>
  );
}
